import { useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../../../configs/translations'
import { StatusBadge } from '../../../mdfActivitiesTable/components/statusBadge'
import { formatAmount, IAllocationBreakdownRow } from '../../helpers'

import styles from './BadgeField.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.detailsModal

/** Allocation shares are fractional (e.g. `19.23077`) — one decimal is enough here. */
const formatAllocationPercent = (percent: number): string => `${Math.round(percent * 10) / 10} %`

export interface IBadgeFieldProps {
  values: string[]
  /** Per-value amount/share split. Empty when the activity carries no allocations. */
  breakdown: IAllocationBreakdownRow[]
  /** Currency of the breakdown amounts — allocations are only reported in local currency. */
  currencyCode: string
  /**
   * Expansion is owned by the parent so that every badge field of the section
   * opens and closes together — the three splits describe the same money and are
   * meant to be read side by side.
   */
  isExpanded: boolean
  onToggle: () => void
}

/**
 * Renders a details-modal field's values as badges, matching the activity-status
 * treatment. Duplicates are collapsed: the legacy `;`-separated fields repeat a
 * value once per allocation row (e.g. `";Print;Print;Print"`), which is noise in
 * a badge list.
 *
 * When the activity has an allocation split, a chevron beside the badges expands
 * a mini-table with the amount and share allocated to each value.
 */
export const BadgeField = ({
  values,
  breakdown,
  currencyCode,
  isExpanded,
  onToggle,
}: IBadgeFieldProps) => {
  const unique = useMemo(() => Array.from(new Set(values)), [values])

  if (!unique.length) {
    return <span className={styles.empty}>—</span>
  }

  const isExpandable = breakdown.length > 0

  return (
    <>
      <div className={styles.badgeRow}>
        <div className={styles.badges}>
          {unique.map((value) => (
            <StatusBadge key={value} status={value} />
          ))}
        </div>
        {isExpandable && (
          <Button
            className={styles.breakdownToggle}
            iconButton={`kbase ${isExpanded ? 'kbase-chevron-bottom' : 'kbase-chevron-right'}`}
            size="sm"
            variant="text"
            aria-expanded={isExpanded}
            onClick={onToggle}
          />
        )}
      </div>

      {isExpandable && isExpanded && (
        <table className={styles.breakdown}>
          <tbody>
            {breakdown.map((row) => (
              <tr key={row.value}>
                <th scope="row" className={styles.breakdownValue}>
                  {row.value}
                </th>
                <td className={styles.breakdownAmount}>
                  {formatAmount(row.amountLc, false)} {currencyCode}
                </td>
                <td className={styles.breakdownPercent}>
                  <Tooltip
                    tipContent={() => <Translation text={KEYS.fields.hpContributionPercent} span />}
                  >
                    <span>{formatAllocationPercent(row.percent)}</span>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

BadgeField.displayName = 'BadgeField'
